import { MAIN_CONTENT_ID, SkipToMainLink } from '@h2hspark/ui';
import * as React from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { SiteFooter, SiteHeader } from '@/domains/cms/site-chrome-panel';

/**
 * The marketing site's shell.
 *
 * **It holds no copy and no links of its own** (`CMS-014`). The header's navigation and the whole
 * footer come from the single `siteSettings` entry, published like any other content, so changing
 * a footer link is an edit in the console rather than a deploy. The chrome components render the
 * site's defaults whenever a setting is empty, which is why this file needs no fallback of its
 * own — a second source for the footer is exactly the trap ADR 022 rejected for pages.
 */
export function MarketingLayout(): React.ReactElement {
  useScrollForNavigation();

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Ahead of `SiteHeader`, whose navigation is published content and therefore of unknown
          length — the one header on the platform nobody can count the tab stops of (`PLT-007`
          AC-3). */}
      <SkipToMainLink />
      <SiteHeader />
      {/* `tabIndex={-1}` so the skip link moves focus and not only the scroll position; see
          `SkipToMainLink`. `useScrollForNavigation` below is not in its way: that hook runs on a
          *router* navigation, and a plain `<a href="#…">` is a same-document fragment jump the
          browser handles itself — and if it did run, scrolling `#main-content` into view is what
          the link asked for anyway. */}
      <main id={MAIN_CONTENT_ID} tabIndex={-1} className="flex-1">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}

/**
 * Where the window goes when the address changes: to the anchored section, or to the top.
 *
 * ## Why the router does not do this
 *
 * React Router changes the URL without a page load, and a browser only acts on a `#fragment`
 * during a real navigation. So a link to `/#features` — which the prototype's navigation is full
 * of, and which `CMS-014`'s seeded settings therefore reproduce — updates the address bar and
 * moves nothing. The link looks broken, which on a marketing site is worse than not having it.
 *
 * ## Why it retries
 *
 * The section being scrolled to is **published content fetched over the network**, so at the
 * moment the address changes it usually does not exist in the document yet. One `getElementById`
 * on the spot would find nothing on the first visit and work on every subsequent one, which is the
 * kind of bug that never reproduces for the person who has the page cached. It looks again on the
 * next few frames and gives up quietly.
 *
 * An address with no fragment scrolls to the top, because arriving at a new page halfway down it
 * is the other half of what a browser normally does for free.
 */
function useScrollForNavigation(): void {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if (hash === '') {
      window.scrollTo(0, 0);
      return undefined;
    }

    const id = decodeURIComponent(hash.slice(1));
    let frame = 0;
    let attempts = 0;

    const look = () => {
      const target = document.getElementById(id);
      if (target !== null) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      // Roughly a second at 60fps. Long enough for a delivery response, short enough that it is
      // not still hunting when somebody has scrolled somewhere else themselves.
      if (++attempts > 60) return;
      frame = window.requestAnimationFrame(look);
    };

    frame = window.requestAnimationFrame(look);
    return () => window.cancelAnimationFrame(frame);
  }, [pathname, hash]);
}
